import dayjs, { Dayjs } from 'dayjs';
import * as R from 'ramda';
import { SHA3 } from 'sha3';
import urlRegexSafe from 'url-regex-safe';
import { AnnouncementSetting, _Boolean } from '@/API';
import { getSystemSettingInfoIfNotCreate } from '@/appsync/service/SystemSettingQueryService';
import { PERMISSION_KEYS, Permissions } from '@/services/role/roleConstants';
import { getUserRolePermissions } from '@/services/role/roleService';
import { LoginInfo, PaginationDataType } from '@/types/type';
import { millisecondsToSeconds } from '@/utils/AnnouncementUtils';
import { PortalDate, newPortalDate } from '@/utils/PortalDate';
import {
  PAGE_PATH_BY_NAME,
  ApplicationDomain,
  LOCALHOST_8088,
  PORTAL_USER_MANUAL_URL_INTRA,
  PORTAL_USER_MANUAL_URL_COMMERCIAL,
  FBS_USER_MANUAL_URL_INTRA,
  FBS_USER_MANUAL_URL_COMMERCIAL,
} from '@/utils/consts';

export const ifDateAfterCurrent = (givenDate: number | null) => {
  return givenDate ? givenDate >= millisecondsToSeconds(Date.now()) : true;
};

//'givenDate' should be of format AWSTimeStamp
export const getFormattedDate = (givenDate: number) => {
  const standardDate = new Date(givenDate * 1000);
  const mm =
    standardDate.getMonth() + 1 <= 9
      ? '0' + (standardDate.getMonth() + 1)
      : standardDate.getMonth() + 1;
  const dd =
    standardDate.getDate() <= 9
      ? '0' + standardDate.getDate()
      : standardDate.getDate();
  const yyyy = standardDate.getFullYear().toString();
  const formattedDate = yyyy + '/' + mm + '/' + dd;
  return formattedDate;
};

export const getDisabledRoutes = async (
  tenant: string,
  landscape: string,
  loginName: string // ログイン名を必須パラメータに変更
): Promise<string[]> => {
  const systemSettingInfoData = await getSystemSettingInfoIfNotCreate(
    tenant,
    landscape
  );
  const disabledRouteList: string[] = [];

  //By default announcement and event calendar feature is disabled
  if (!systemSettingInfoData) {
    disabledRouteList.push(PAGE_PATH_BY_NAME.commonsettingAnnouncement);
    disabledRouteList.push(PAGE_PATH_BY_NAME.announcementList);
    disabledRouteList.push(PAGE_PATH_BY_NAME.announcementWithId);
    disabledRouteList.push(PAGE_PATH_BY_NAME.eventCalendar);
    disabledRouteList.push(PAGE_PATH_BY_NAME.eventCalendarForMonth);
    disabledRouteList.push(PAGE_PATH_BY_NAME.jobs);
    disabledRouteList.push(PAGE_PATH_BY_NAME.createJob);
    disabledRouteList.push(PAGE_PATH_BY_NAME.job);
    disabledRouteList.push(PAGE_PATH_BY_NAME.commonsettingWebApi);
    disabledRouteList.push(PAGE_PATH_BY_NAME.eventList);
  }

  if (systemSettingInfoData && !systemSettingInfoData.isJobSchedulerEnabled) {
    disabledRouteList.push(PAGE_PATH_BY_NAME.eventCalendar);
    disabledRouteList.push(PAGE_PATH_BY_NAME.eventCalendarForMonth);
    disabledRouteList.push(PAGE_PATH_BY_NAME.jobs);
    disabledRouteList.push(PAGE_PATH_BY_NAME.createJob);
    disabledRouteList.push(PAGE_PATH_BY_NAME.job);
    disabledRouteList.push(PAGE_PATH_BY_NAME.commonsettingWebApi);
    disabledRouteList.push(PAGE_PATH_BY_NAME.eventList);
  }

  // Announcement is enabled only when systemSettingInfoData.announcementSetting is set as UseV2
  if (
    systemSettingInfoData &&
    systemSettingInfoData.announcementSetting !== AnnouncementSetting.UseV2
  ) {
    disabledRouteList.push(PAGE_PATH_BY_NAME.commonsettingAnnouncement);
    disabledRouteList.push(PAGE_PATH_BY_NAME.announcementList);
    disabledRouteList.push(PAGE_PATH_BY_NAME.announcementWithId);
  }

  // ロールに基づく権限を適用
  try {
    await applyRoleBasedPermissions(
      loginName,
      tenant,
      landscape,
      disabledRouteList
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('ユーザー権限の取得中にエラーが発生しました:', error);
  }

  // 重複を削除して返す
  const uniqueRoutes = Array.from(new Set(disabledRouteList));
  return uniqueRoutes;
};

/**
 * ユーザーのロールと権限に基づいて、アクセス制限が必要なルートを追加する
 * @param loginName ユーザーのログイン名
 * @param tenant テナント名
 * @param landscape 環境名
 * @param disabledRouteList 制限対象のルートリスト
 */
export const applyRoleBasedPermissions = async (
  loginName: string,
  tenant: string,
  landscape: string,
  disabledRouteList: string[]
): Promise<void> => {
  const permissions: Permissions = await getUserRolePermissions(
    loginName,
    tenant,
    landscape
  );

  // スケジューラー権限がない場合の制限
  if (
    !(
      permissions &&
      permissions.menus &&
      permissions.menus[PERMISSION_KEYS.MENUS.SCHEDULER]
    )
  ) {
    disabledRouteList.push(PAGE_PATH_BY_NAME.eventCalendar);
    disabledRouteList.push(PAGE_PATH_BY_NAME.eventCalendarForMonth);
    disabledRouteList.push(PAGE_PATH_BY_NAME.eventList);
    disabledRouteList.push(PAGE_PATH_BY_NAME.createEvent);
    disabledRouteList.push(PAGE_PATH_BY_NAME.editEvent);
    disabledRouteList.push(PAGE_PATH_BY_NAME.copyEvent);
    disabledRouteList.push(PAGE_PATH_BY_NAME.viewEvent);
    disabledRouteList.push(PAGE_PATH_BY_NAME.createHoliday);
    disabledRouteList.push(PAGE_PATH_BY_NAME.editHoliday);
    disabledRouteList.push(PAGE_PATH_BY_NAME.copyHoliday);
    disabledRouteList.push(PAGE_PATH_BY_NAME.viewHoliday);
  }

  // ジョブ権限がない場合の制限
  if (
    !(
      permissions &&
      permissions.menus &&
      permissions.menus[PERMISSION_KEYS.MENUS.JOB]
    )
  ) {
    disabledRouteList.push(PAGE_PATH_BY_NAME.jobs);
    disabledRouteList.push(PAGE_PATH_BY_NAME.createJob);
    disabledRouteList.push(PAGE_PATH_BY_NAME.job);
  }

  // 設定権限がない場合の制限
  if (
    !(
      permissions &&
      permissions.menus &&
      permissions.menus[PERMISSION_KEYS.MENUS.SETTINGS]
    )
  ) {
    disabledRouteList.push(PAGE_PATH_BY_NAME.commonsetting);
    disabledRouteList.push(PAGE_PATH_BY_NAME.commonsettingGeneral);
    disabledRouteList.push(PAGE_PATH_BY_NAME.commonsettingWebApi);
    disabledRouteList.push(PAGE_PATH_BY_NAME.commonsettingAnnouncement);
    disabledRouteList.push(PAGE_PATH_BY_NAME.commonsettingUserManagement);
    disabledRouteList.push(PAGE_PATH_BY_NAME.commonsettingRole);
  }

  // ホーム権限がない場合の制限
  if (
    !(
      permissions &&
      permissions.menus &&
      permissions.menus[PERMISSION_KEYS.MENUS.HOME]
    )
  ) {
    disabledRouteList.push(PAGE_PATH_BY_NAME.portal);
  }

  // サイトマップ権限がない場合の制限
  if (
    !(
      permissions &&
      permissions.menus &&
      permissions.menus[PERMISSION_KEYS.MENUS.SITEMAP]
    )
  ) {
    disabledRouteList.push(PAGE_PATH_BY_NAME.sitemap);
    disabledRouteList.push(PAGE_PATH_BY_NAME.search);
  }
};

export const getSHA3of = (textString: string) => {
  const SHA3_BIT = 256;
  const SHA3_DIGEST = 'hex';
  const sha3 = new SHA3(SHA3_BIT);
  const SHA3String = sha3.update(textString).digest(SHA3_DIGEST);
  sha3.reset();
  return SHA3String;
};

export const getSHA3OfTenantLandscape = (tenant: string, landscape: string) =>
  getSHA3of(joinWithHash(tenant, landscape));

export const joinWithHash = (...xs: string[]): string => R.join('#')([...xs]);

export const checkBottomAndFetchData = async (
  allData: unknown[],
  idField: string,
  fetchDataFunc: (loginInfo: LoginInfo, nextToken: string | undefined) => any,
  loginInfo: LoginInfo,
  nextToken: string | undefined
) => {
  const bottomOfWindow =
    document.documentElement.scrollTop +
      document.documentElement.clientHeight >=
    document.documentElement.scrollHeight;
  if (bottomOfWindow && nextToken) {
    const fetchedData = await fetchData(
      allData,
      idField,
      fetchDataFunc,
      loginInfo,
      nextToken
    );
    return fetchedData;
  }
  return null;
};

const fetchData = async (
  allData: unknown[],
  idField: string,
  fetchDataFunc: (loginInfo: LoginInfo, nextToken: string | undefined) => any,
  loginInfo: LoginInfo,
  nextToken: string | undefined
) => {
  const fetchedData: PaginationDataType = await fetchDataFunc(
    loginInfo,
    nextToken
  );
  fetchedData.items.forEach(newData => pushUnique(allData, newData, idField));
  nextToken = fetchedData.nextToken;
  return { items: allData, nextToken: nextToken };
};

export const pushUnique = (allData: any[], newData: any, idField: string) => {
  if (allData.findIndex(other => other[idField] === newData[idField]) === -1) {
    allData.push(newData);
  }
  return allData;
};

export const unshiftUnique = (
  allData: any[],
  newData: any,
  idField: string
) => {
  if (allData.findIndex(other => other[idField] === newData[idField]) === -1) {
    allData.unshift(newData);
  }
  return allData;
};

export const isValidUrl = (url: string) =>
  RegExp('^https?://', 'i').test(url) && //test if starts with http:// or https://
  urlRegexSafe({ auth: true, exact: true, parens: true, strict: true }).test(
    url
  ); //url regex check

export const wrapNilInput = R.curry(R.ifElse)(R.isNil);

export const returnFalseIfNilElseProcess: (
  onFalse: (x: any) => any
) => (x: any) => any = wrapNilInput(R.always(false));

export const boolean2_BooleanEnum = (arg: boolean) => {
  return arg ? _Boolean._true : _Boolean._false;
};

export const _BooleanEnum2boolean = (arg: _Boolean | string) => {
  const _BooleanValues: string[] = Object.values(_Boolean);
  if (!_BooleanValues.includes(arg)) {
    throw Error('The passed value is neither _true nor _false');
  }
  return arg === _Boolean._true;
};

export const getFirstDayOfThisMonth = (date: PortalDate) => {
  return newPortalDate(date.toDayjs().startOf('month'), '');
};

export const getEndDayOfThisMonth = (date: PortalDate) => {
  return newPortalDate(date.toDayjs().endOf('month'), '');
};

export const isDate: (
  x: Dayjs | Date | string | number | unknown
) => boolean = R.is(Date);
export const isString: (
  x: Dayjs | Date | string | number | unknown
) => boolean = R.is(String);
export const isNumber = R.is(Number);
const gteZero = R.lte(0);
const isNotNaN = R.complement(isNaN);
const isInteger = Number.isInteger;
export const isWholeNumber = R.allPass([isInteger, isNotNaN, gteZero]);
export const isNonNaNInteger = R.allPass([isInteger, isNotNaN]);

export const isNotEmpty = R.complement(R.isEmpty);
export const isNotEmptyArray = R.allPass([isNotEmpty, Array.isArray]);
export const isEmptyArray = R.allPass([Array.isArray, R.isEmpty]);
export const isNotEmptyString = R.allPass([isNotEmpty, isString]);
export const filterByNotEmptyString = R.filter(isNotEmpty);

export const isNumeric = R.allPass([
  R.pipe(parseFloat, R.complement(isNaN)),
  isFinite,
]);

export const ifNilThenDefaultTo = (input: any, defaultValue: any) => {
  return !R.isNil(input) ? input : defaultValue;
};

export const fixedEncodeURIComponent = (str: string) => {
  const specialSymbolsRegex = /[!'()*]/g;
  return encodeURIComponent(str).replace(specialSymbolsRegex, c => {
    return (
      '%' +
      c
        .charCodeAt(0)
        .toString(16)
        .toUpperCase()
    );
  });
};

export const getTimezoneOffset = () => {
  return new Date(Date.now()).getTimezoneOffset();
};

export const isAccessedFromIosDeviceOrSafariBrowser = () => {
  const userAgentData = window.navigator.userAgentData;
  if (userAgentData) {
    //only Chrome and Edge in MacOS or Windows supports userAgentData
    //both of them are recommended browser
    return false;
  } else {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isAccessedFromIosDeviceOrSafariBrowser = /mac/.test(userAgent);
    return isAccessedFromIosDeviceOrSafariBrowser;
  }
};

export const getUserManualUrl = () => {
  const hostname = location.hostname;
  if (hostname.endsWith(ApplicationDomain.localhost)) {
    return LOCALHOST_8088;
  } else if (hostname.endsWith(ApplicationDomain.portalIntra)) {
    return PORTAL_USER_MANUAL_URL_INTRA;
  } else if (hostname.endsWith(ApplicationDomain.fbsIntra)) {
    return FBS_USER_MANUAL_URL_INTRA;
  } else if (hostname.endsWith(ApplicationDomain.fbsCommercial)) {
    return FBS_USER_MANUAL_URL_COMMERCIAL;
  } else if (hostname.endsWith(ApplicationDomain.portalCommercial)) {
    return PORTAL_USER_MANUAL_URL_COMMERCIAL;
  }
  return PORTAL_USER_MANUAL_URL_COMMERCIAL;
};

export const isAllUnique = R.converge(R.equals, [R.identity, R.uniq]);

export const isToday = (date: string): boolean => {
  return date === newPortalDate(dayjs(), 'YYYY-MM-DD').toString();
};
